import { ButtonHTMLAttributes, useEffect, useRef, useState } from 'react'
import Flip from 'gsap/dist/Flip'

import Button from '@/components/base/button'
import Spinner from '@/components/base/spinner'

import { cn } from '@/lib/utils'

export type FormSubmitProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  isSubmitting?: boolean
}

const FormSubmit = ({ className, children, isSubmitting, ...props }: FormSubmitProps) => {
  const [active, setActive] = useState(false)
  const rect = useRef<HTMLSpanElement>(null!)
  const wrapper = useRef<HTMLSpanElement>(null!)

  useEffect(() => {
    const state = Flip.getState(rect.current)
    wrapper.current.classList[active ? 'add' : 'remove']('active')
    Flip.from(state, { duration: 0.5, ease: 'power2.out' })
  }, [active])

  return (
    <Button
      type="submit"
      variant="secondary"
      disabled={isSubmitting}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
      className={cn('relative mt-[5.2rem]', className)}
      endIcon={
        <>
          {isSubmitting ? (
            <div className="!absolute right-[2.5rem] top-1/2 -translate-y-1/2">
              <Spinner variant="small" />
            </div>
          ) : null}
          <span
            ref={wrapper}
            className={cn('relative [&.active]:static', {
              'invisible opacity-0': isSubmitting
            })}
          >
            <span
              ref={rect}
              className="absolute left-0 top-0 !m-0 size-full rounded-[0.5rem] bg-dark dark:bg-white"
            />
            <svg className="relative size-[2.6rem]" viewBox="0 0 26 26" fill="none">
              <rect
                className="fill-dark dark:fill-white"
                x="0.5"
                y="0.5"
                width="25"
                height="25"
                rx="2"
              />
              <rect
                className={cn(
                  'fill-white dark:fill-dark',
                  'transition-transform duration-500 ease-out-cubic',
                  'origin-center scale-0 group-hover/button:scale-100'
                )}
                x="0.5"
                y="0.5"
                width="25"
                height="25"
                rx="2"
              />
              <path
                d="M12.272 16.5V13.728H9.5V12.272H12.272V9.5H13.728V12.272H16.5V13.728H13.728V16.5H12.272Z"
                className={cn(
                  'fill-white dark:fill-dark',
                  'origin-center transition-all duration-300 ease-out-cubic',
                  'group-hover/button:rotate-90 group-hover/button:fill-dark dark:group-hover/button:fill-white'
                )}
              />
            </svg>
          </span>
        </>
      }
      {...props}
    >
      {children}
    </Button>
  )
}

export default FormSubmit
